import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import styles from './styles.module.scss';
import Button from '../Button/index.ts';
import addVariants from '../utils/addVariants/index.ts';
import useBoolean from '../utils/useBoolean/index.ts';

const ICONS = {
  CLOSE: faTimes
};

export const TYPES = ['muted', 'info', 'warning', 'critical'];

export const VARIANTS = ['default', 'no-border'];

function Alert({
  type,
  children,
  onChange,
  onOpen,
  onClose,
  isOpen: isOpenFromProps,
  isDismissable,
  isOpenByDefault,
  variant,
  className
}) {
  const { value: isOpen, setFalse: close } = useBoolean(isOpenByDefault, {
    onChange: isOpenFromState => {
      if (isOpenFromState) {
        onOpen();
      } else {
        onClose();
      }

      onChange(isOpenFromState);
    },
    overrideWith: isOpenFromProps
  });

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={classnames(
        addVariants('alert', styles, type, variant, isDismissable && 'dismissable'),
        className
      )}
    >
      <span className={styles.alert__payload}>{children}</span>
      {isDismissable && (
        <Button
          className={styles.alert__cancel}
          variant={['round', 'no-border']}
          color="hover-darken"
          icon={ICONS.CLOSE}
          onClick={close}
        />
      )}
    </div>
  );
}

Alert.propTypes = {
  /**
   * The type of alert to display
   */
  type: PropTypes.oneOf(TYPES).isRequired,
  /**
   * Anything to render as an alert
   */
  children: PropTypes.node.isRequired,
  /**
   * Fires on close
   * @deprecated Use `onChange` instead
   */
  onClose: PropTypes.func,
  /**
   * Fires on open
   * @deprecated Use `onChange` instead
   */
  onOpen: PropTypes.func,
  /**
   * Fires on change of open state
   */
  onChange: PropTypes.func,
  /**
   * Controls open state (state must be handled in caller)
   */
  isOpen: PropTypes.bool,
  /**
   * Whether the alert is dismissable
   */
  isDismissable: PropTypes.bool,
  /**
   * Whether the alert is open by default
   */
  isOpenByDefault: PropTypes.bool,
  /**
   * Decides the variant of the button
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(VARIANTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(VARIANTS), PropTypes.bool]))
  ]),
  /**
   * Additional classnames to apply
   */
  className: PropTypes.string
};

Alert.defaultProps = {
  isDismissable: true,
  variant: 'default',
  className: '',
  onChange: () => 0,
  onOpen: () => 0,
  onClose: () => 0,
  isOpenByDefault: true,
  isOpen: null
};

export default Alert;
